// =============================================================================
// VINUM - Notification Store
// Manages notification count and recent notifications for the bell icon
// =============================================================================

import { create } from 'zustand';
import { NotificationsAPI } from '../api/notifications.api';
import type { Notification } from '../api/notifications.api';

interface NotificationState {
  unreadCount: number;
  totalCount: number;
  recentNotifications: Notification[];
  isLoading: boolean;
  lastFetched: number | null;

  // Actions
  fetchCount: () => Promise<void>;
  fetchRecent: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  addNotification: (notification: Notification) => void;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  unreadCount: 0,
  totalCount: 0,
  recentNotifications: [],
  isLoading: false,
  lastFetched: null,

  fetchCount: async () => {
    try {
      const { total, unread } = await NotificationsAPI.getMyNotificationsCount();
      set({ unreadCount: unread, totalCount: total, lastFetched: Date.now() });
    } catch {
      // Silently fail — bell just stays at 0
    }
  },

  fetchRecent: async () => {
    set({ isLoading: true });
    try {
      const resp = await NotificationsAPI.getMyNotifications({ limit: 10, skip: 0 });
      set({
        recentNotifications: resp.notifications,
        unreadCount: resp.unread,
        totalCount: resp.total,
        lastFetched: Date.now(),
      });
    } catch {
      // Silently fail
    } finally {
      set({ isLoading: false });
    }
  },

  markAsRead: async (id: string) => {
    try {
      await NotificationsAPI.markAsRead(id);
      set((state) => ({
        unreadCount: Math.max(0, state.unreadCount - 1),
        recentNotifications: state.recentNotifications.map((n) =>
          n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n
        ),
      }));
    } catch {
      // Silently fail
    }
  },

  markAllAsRead: async () => {
    try {
      await NotificationsAPI.markAllAsRead();
      set((state) => ({
        unreadCount: 0,
        recentNotifications: state.recentNotifications.map((n) => ({
          ...n,
          read: true,
          readAt: n.readAt || new Date().toISOString(),
        })),
      }));
    } catch {
      // Silently fail
    }
  },

  addNotification: (notification: Notification) => {
    set((state) => ({
      unreadCount: state.unreadCount + 1,
      totalCount: state.totalCount + 1,
      recentNotifications: [notification, ...state.recentNotifications].slice(0, 10),
    }));
  },

  reset: () => {
    set({
      unreadCount: 0,
      totalCount: 0,
      recentNotifications: [],
      isLoading: false,
      lastFetched: null,
    });
  },
}));

// Selectors
export const selectUnreadCount = (state: NotificationState) => state.unreadCount;
